import { useCallback, useEffect, useMemo, useState } from 'react';
import { listCoreMedia, mediaTypeMeta, type CoreMedia } from '@/lib/coreSocle';
import Modal from '@/pages/dashboard/core/components/Modal';

interface MediaLibraryPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Propriétaire des médias (id commerce). */
  ownerId: number;
  /** Callback avec les URL choisies. */
  onSelect: (urls: string[]) => void;
  /** Sélection multiple (défaut) ou unique. */
  multi?: boolean;
  /** URL déjà utilisées, pour les marquer comme sélectionnées. */
  initialSelected?: string[];
}

/**
 * Sélecteur branché sur le socle Média central (`images`).
 * Permet aux fiches Produit/Service de réutiliser des médias déjà
 * référencés au lieu de re-téléverser des fichiers séparés.
 */
export default function MediaLibraryPicker({
  isOpen,
  onClose,
  ownerId,
  onSelect,
  multi = true,
  initialSelected = [],
}: MediaLibraryPickerProps) {
  const [media, setMedia] = useState<CoreMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>(initialSelected);

  const load = useCallback(async () => {
    if (!ownerId) {
      setMedia([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rows = await listCoreMedia(ownerId);
      setMedia(rows.filter((m) => (m.media_type || 'image') === 'image' && m.filepath));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    if (isOpen) {
      setSelected(initialSelected);
      load();
    }
  }, [isOpen, load, initialSelected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return media;
    return media.filter((m) => (m.title || '').toLowerCase().includes(q) || (m.categorie || '').toLowerCase().includes(q));
  }, [media, query]);

  const toggle = (url: string) => {
    if (!multi) {
      onSelect([url]);
      onClose();
      return;
    }
    setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));
  };

  const confirm = () => {
    if (selected.length > 0) onSelect(selected);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Médiathèque"
      onClose={onClose}
      maxWidth="max-w-4xl"
      footer={
        <>
          <span className="text-xs text-foreground-500 mr-auto">{selected.length} sélectionné{selected.length > 1 ? 's' : ''}</span>
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
          <button
            onClick={confirm}
            disabled={multi && selected.length === 0}
            className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
          >
            <i className="ri-check-line"></i> {multi ? 'Ajouter à la fiche' : 'Choisir'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un média…"
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>

        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-image-2-line text-2xl text-foreground-400"></i></div>
            <p className="text-sm text-foreground-600 font-medium">Aucune image dans la médiathèque.</p>
            <p className="text-xs text-foreground-500 mt-1">Téléversez des images depuis <span className="font-semibold text-foreground-700">Socle central → Média</span>, elles apparaîtront ici.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[52vh] overflow-y-auto pr-1">
            {filtered.map((m) => {
              const isSelected = selected.includes(m.filepath);
              const tm = mediaTypeMeta(m.media_type);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggle(m.filepath)}
                  className={`relative rounded-lg overflow-hidden border transition-all cursor-pointer group ${isSelected ? 'border-primary-400 ring-2 ring-primary-300' : 'border-background-200/70 hover:border-primary-300/60'}`}
                >
                  <div className="aspect-[4/3] bg-background-100 overflow-hidden">
                    <img src={m.filepath} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                  )}
                  <div className="p-2 text-left bg-background-50">
                    <p className="text-xs font-medium text-foreground-800 truncate">{m.title}</p>
                    <p className="text-[10px] text-foreground-400 inline-flex items-center gap-1"><i className={tm.icon}></i>{m.categorie || 'sans catégorie'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}